'use client';

import React from 'react';
import { Icon, type IconName } from '../../icons';
import './DetailPanel.css';

export type DetailPanelState = 'empty' | 'detail' | 'bulk';

export interface DetailPanelProps {
  state: DetailPanelState;
  title?: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: string | number;
  className?: string;
  onClose?: () => void;

  emptyIcon?: IconName;
  emptyTitle?: string;
  emptyDescription?: string;

  bulkCount?: number;
  bulkChildren?: React.ReactNode;
}

export function DetailPanel({
  state,
  title,
  subtitle,
  headerActions,
  actions,
  tabs,
  children,
  footer,
  width = 420,
  className = '',
  onClose,
  emptyIcon = 'MousePointerClick',
  emptyTitle = 'No item selected',
  emptyDescription = 'Select an item to view details',
  bulkCount = 0,
  bulkChildren,
}: DetailPanelProps) {
  const cssWidth = typeof width === 'number' ? `${width}px` : width;

  if (state === 'empty') {
    return (
      <aside
        className={`detail-panel ${className}`.trim()}
        style={{ '--detail-panel-width': cssWidth } as React.CSSProperties}
      >
        <div className="detail-panel__empty">
          <div className="detail-panel__empty-icon">
            <Icon name={emptyIcon} size={32} />
          </div>
          <p className="detail-panel__empty-title">{emptyTitle}</p>
          <p className="detail-panel__empty-description">{emptyDescription}</p>
        </div>
      </aside>
    );
  }

  if (state === 'bulk') {
    return (
      <aside
        className={`detail-panel ${className}`.trim()}
        style={{ '--detail-panel-width': cssWidth } as React.CSSProperties}
      >
        <div className="detail-panel__header">
          <div className="detail-panel__header-text">
            <h2 className="detail-panel__title">{bulkCount} selected</h2>
          </div>
          {onClose && (
            <div className="detail-panel__header-actions">
              <button className="detail-panel__close" aria-label="Deselect all" onClick={onClose}>
                <Icon name="X" size={20} />
              </button>
            </div>
          )}
        </div>
        <div className="detail-panel__bulk">
          {bulkChildren && (
            <div className="detail-panel__bulk-actions">
              {bulkChildren}
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={`detail-panel ${className}`.trim()}
      style={{ '--detail-panel-width': cssWidth } as React.CSSProperties}
    >
      {(title || headerActions) && (
        <div className="detail-panel__header">
          <div className="detail-panel__header-text">
            {title && <h2 className="detail-panel__title">{title}</h2>}
            {subtitle && <p className="detail-panel__subtitle">{subtitle}</p>}
          </div>
          {headerActions && (
            <div className="detail-panel__header-actions">
              {headerActions}
            </div>
          )}
        </div>
      )}

      {actions && <div className="detail-panel__actions">{actions}</div>}

      {tabs && <div className="detail-panel__tabs">{tabs}</div>}

      <div className="detail-panel__body">{children}</div>

      {footer && <div className="detail-panel__footer">{footer}</div>}
    </aside>
  );
}
